import { BeneficiaryType } from 'dto/beneficiary';
import { type CoveragePremium } from 'dto/CoveragePremium';
import { CoverageProduct } from 'dto/marketableProduct';
import { getCurrentLanguage } from 'i18n';
import { type SelectedSumInsuredPremiums } from 'individual/types/selectedSumInsuredPremiums';
import { getSuggestedPremium } from 'utils';

window.adobeAnalyticsData = window.adobeAnalyticsData || {};
window._satellite = window._satellite || {};

if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
  window.adobe = {};
  // if not already stubbed in cypress test
  if (!window._satellite.stubbed) {
    // eslint-disable-next-line
    window._satellite = {
      track: (phase, payload) => {
        console.log(phase, payload);
      },
    };

    window.adobeAnalyticsData = {
      pageName: 'localhost',
    };
  }
}

export const isLaunchReady = () =>
  typeof window._satellite?.track === 'function';
const lang = getCurrentLanguage();

const languageMap: Record<string, string> = {
  fi: 'fi_fi',
  sv: 'sv_se',
  en: 'en_us',
};

const language = languageMap[lang] ?? 'fi_fi';
const customer = 'ha'; // ha
const applicationAction = 'application_action';
const applicationPhase = 'application_phase';

export enum AnalyticsPhases {
  CALCULATE_PRICE = '1. laske hinta',
  PRICE = '3. vakuutuksesi_hinta',
  RESTRICTED_ACCESS = 'palveluita rajoitettu',
  BENEFICIARIES_AND_DOCUMENTS = '4. Edunsaajat_ja_asiakirjat',
  PURCHASE_CONFIRMED = '5. Osto_vahvistettu',
}

export enum AnalyticsPhaseCategories {
  START = 'start',
  STEP = 'step',
  COMPLETED = 'completed',
}

const analyticsBeneficiaries: Record<BeneficiaryType, string> = {
  [BeneficiaryType.SPOUSE]: 'kuolinturva-edunsaajaaviopuoliso',
  [BeneficiaryType.SPOUSE_AND_CHILDREN]:
    'kuolinturva-edunsaajaaviopuolisojalapset',
  [BeneficiaryType.CHILDREN]: 'kuolinturva-edunsaajalapset',
  [BeneficiaryType.NEXT_OF_KIN]: 'kuolinturva-edunsaajaomaiset',
  [BeneficiaryType.DEATH_ESTATE]: 'kuolinturva-edunsaajakuolinpesa',
  [BeneficiaryType.NAMED_BENEFICIARY]: 'kuolinturva-edunsaajanimetytprosentti',
};

const getAuthTypeForAnalytics = () => {
  if (!isUserAuthenticated()) {
    return '';
  }
  const authMethod = window.adobeAnalyticsData.authMethod ?? '';
  if (authMethod.includes('MOB')) {
    return 'mobilekey';
  } else if (authMethod.includes('TUPAS')) {
    return authMethod;
  } else {
    return 'keycardlist';
  }
};

const isUserAuthenticated = () => {
  return window.__RHVKLSO002_INITIAL_STATE?.authenticatedCustomer;
};

const applicationParams = {
  language,
  name: `vv ${customer} henkivakuutus`,
  group: 'purchase',
  business_group: 'insurance',
  channel: customer,
};

const isOwnerCustomer = () => {
  return window.__RHVKLSO002_INITIAL_STATE?.partyInformation?.ownerCustomer;
};

function analyticsCall(fn: (...args: any) => void) {
  return (...args: any) => {
    if (!isLaunchReady()) return;
    try {
      // @ts-expect-error missing typings
      fn.call(this, ...args);
    } catch (err) {
      console.warn('Analytics call failed:', err);
    }
  };
}

const resolveCustomerInfo = (sum: number | undefined) => {
  const isOwner = isOwnerCustomer();
  const infoText = isOwner ? '-omistajaasiakas' : '';
  return `korvaus${sum ?? ''}${infoText}`;
};

export const insuranceProductName: Record<CoverageProduct, string> = {
  [CoverageProduct.LIFE]: 'henkivakuutus',
  [CoverageProduct.CRITICAL_ILLNESS]: 'vakavansairaudenturva',
  [CoverageProduct.TEMPORARY_DISABILITY]: 'ohimeneva_tyokyvyttomyys',
  [CoverageProduct.PERMANENT_DISABILITY]: 'pysyva_tyokyvyttomyys',
};

export const resolveProducts = ({
  selectedSumInsuredPremiums,
  beneficiaryInfo,
  selectedCoverages,
}: {
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums;
  beneficiaryInfo: BeneficiaryType | undefined;
  selectedCoverages: CoverageProduct[];
}) =>
  selectedCoverages
    .map((coverage) => {
      const { annualNetPremium, sumInsured, deductibleDays } =
        selectedSumInsuredPremiums[coverage] ?? {};
      const eVar59 = `|eVar59=${resolveCustomerInfo(sumInsured)}`;

      let eVar67 = '';
      if (coverage === CoverageProduct.LIFE && beneficiaryInfo) {
        eVar67 = `|eVar67=${analyticsBeneficiaries[beneficiaryInfo]}`;
      } else if (
        coverage === CoverageProduct.TEMPORARY_DISABILITY &&
        deductibleDays
      ) {
        eVar67 = `|eVar67=omavastuu${deductibleDays}pv`;
      }

      return `vakuutukset;${insuranceProductName[coverage]};1;${annualNetPremium?.amount};;eVar66=aikuinen itselle${eVar59}${eVar67}`;
    })
    .join(',');

export interface AnalyticsProductProps {
  coveragePremiums: CoveragePremium[] | undefined;
  selectedSumInsuredPremiums: SelectedSumInsuredPremiums;
  beneficiaryInfo?: BeneficiaryType;
  selectedCoverages: CoverageProduct[];
}

export const formProductsToParams = ({
  products,
}: {
  products: AnalyticsProductProps;
}) => {
  const {
    coveragePremiums,
    selectedSumInsuredPremiums,
    beneficiaryInfo,
    selectedCoverages,
  } = products;
  const params: any = {};
  const selectedCoveragePremiums = coveragePremiums?.filter((coverage) =>
    selectedCoverages.includes(coverage.coverageProduct)
  );
  if (selectedCoveragePremiums) {
    params.products = resolveProducts({
      selectedSumInsuredPremiums,
      beneficiaryInfo,
      selectedCoverages,
    });
    params.decision = selectedCoverages
      .map(
        (coverage) =>
          getSuggestedPremium(selectedCoveragePremiums, coverage)?.sumInsured
      )
      .join(', ');
  }
  return params;
};

// track actions

export const trackAction = analyticsCall(
  (
    actionName: string,
    actionGroup: string,
    phase: AnalyticsPhases,
    products?: AnalyticsProductProps | string
  ) => {
    let productsData: any = {};
    if (typeof products === 'string') {
      productsData.products = products;
    } else if (products) {
      productsData = formProductsToParams({ products });
    }
    const params: any = {
      ...applicationParams,
      action_name: actionName,
      action_group: actionGroup,
      phase,
      auth_type: getAuthTypeForAnalytics(),
      ...productsData,
    };

    window._satellite.track(applicationAction, params);
  }
);

// track phases

export const trackPhase = analyticsCall(
  (
    phaseCategory: AnalyticsPhaseCategories,
    phase: AnalyticsPhases,
    products?: AnalyticsProductProps | string
  ) => {
    let productsData: any = {};
    if (typeof products === 'string') {
      productsData.products = products;
    } else if (products) {
      productsData = formProductsToParams({ products });
    }
    const params: any = {
      ...applicationParams,
      phase_category: phaseCategory,
      phase,
      auth_type: getAuthTypeForAnalytics(),
      ...productsData,
    };

    if (phase === AnalyticsPhases.PURCHASE_CONFIRMED) {
      params.purchase_id = formPurchaseId();
    } else if (phase === AnalyticsPhases.RESTRICTED_ACCESS) {
      params.group = 'contract';
    }

    window._satellite.track(applicationPhase, params);
  }
);

export function formPurchaseId() {
  const salt = Math.random().toString().substring(2, 15);
  return `${new Date().getTime()}${salt}`;
}

export const trackAdformOnStart = analyticsCall(() => {
  window._satellite.track('adform_conversion', {
    conversion_category: `Sovellus_alku_${isUserAuthenticated() ? 'tun' : 'ano'}_henkivakuutusjavakavasairaus`,
  });
});

export const trackAdformOnComplete = analyticsCall(() => {
  window._satellite.track('adform_conversion', {
    conversion_category: 'Sovellus_loppu_henkivakuutusjavakavasairaus',
  });
});
